// src/pages/ProjectDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useData } from "../DataContext";
import { projectDisplayLabelPretty } from "../utils/format";
import { apiRequest } from "../api/http";
import { useTranslation } from "react-i18next";

import OverviewTab from "./projectDetailTabs/OverviewTab";
import ScopeTab from "./projectDetailTabs/ScopeTab";
import PaymentsReceivedTab from "./projectDetailTabs/PaymentsReceivedTab";
import ExpensesTab from "./projectDetailTabs/ExpensesTab";
import WagesTab from "./projectDetailTabs/WagesTab";
import ClientDecisionsTab from "./projectDetailTabs/ClientDecisionsTab";
import PartnerSettlementTab from "./projectDetailTabs/PartnerSettlementTab";

type TabKey =
  | "Overview"
  | "Scope"
  | "Payments Received"
  | "Expenses"
  | "Wages"
  | "Client Decisions"
  | "Partner Settlement";

// -----------------------------
// tab/query helpers
// -----------------------------
function tabFromQueryParam(tabParam: string | null): TabKey {
  const t = (tabParam || "").trim().toLowerCase();
  if (t === "overview") return "Overview";
  if (t === "scope") return "Scope";
  if (t === "payments") return "Payments Received";
  if (t === "expenses") return "Expenses";
  if (t === "wages") return "Wages";
  if (t === "decisions") return "Client Decisions";
  if (t === "settlement") return "Partner Settlement";
  return "Overview";
}

function queryParamFromTab(tab: TabKey): string {
  switch (tab) {
    case "Overview":
      return "overview";
    case "Scope":
      return "scope";
    case "Payments Received":
      return "payments";
    case "Expenses":
      return "expenses";
    case "Wages":
      return "wages";
    case "Client Decisions":
      return "decisions";
    case "Partner Settlement":
      return "settlement";
    default:
      return "overview";
  }
}

// -----------------------------
// API types
// -----------------------------
type ApiProject = {
  id: string;
  tenant_id: string;
  client_id: string;
  name: string;
  address: string;
  status: string | null;
  archived: number; // 0/1
  created_at?: string;
  updated_at?: string;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

// Project-like shape used by existing tabs/utils
type ProjectLike = {
  id: string;
  clientId: string;
  name: string;
  address: string;
  status: string;
  archived: boolean;
  totalEstimate: number;
  received: number;
};

// -----------------------------
// Helpers for mixed key styles
// -----------------------------
function getProjectIdFromRow(row: any): string {
  return (row?.projectId ?? row?.project_id ?? "").toString();
}

export default function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    tenantId,
    clients,

    // still local while migrating
    scopeItems,
    paymentsReceived,
    subContracts,
    wageEntries,
    resources,
    partners,
    partnerDisbursements,
    addPartnerDisbursement,
    updateProject,
    projectFinancialsByProject,
    refreshProjectData,
  } = useData() as any;

  const pid = (projectId || "").trim();

  // active tab from URL
  const urlTab: TabKey = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return tabFromQueryParam(sp.get("tab"));
  }, [location.search]);

  const [activeTab, setActiveTab] = useState<TabKey>(urlTab);

  useEffect(() => {
    setActiveTab(urlTab);
  }, [urlTab]);

  // -----------------------------
  // API-backed project load
  // -----------------------------
  const [apiProject, setApiProject] = useState<ApiProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!pid) {
        setApiProject(null);
        setLoadError("Missing project id.");
        return;
      }

      setLoading(true);
      setLoadError(null);

      try {
        const res = await apiRequest<ApiOk<ApiProject> | ApiErr>(
          "GET",
          `/api/projects/${encodeURIComponent(pid)}`,
          { tenantId }
        );

        if (cancelled) return;

        if ((res as any)?.ok) {
          setApiProject((res as any).data as ApiProject);
        } else {
          setApiProject(null);
          setLoadError((res as any)?.error || "Project not found.");
        }
      } catch (e: any) {
        if (cancelled) return;
        setApiProject(null);
        setLoadError(e?.message || "Failed to load project.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [pid, tenantId]);

  // -----------------------------
  // Project-scoped local data
  // -----------------------------
  const scopeForProject = useMemo(() => {
    return (scopeItems || []).filter((s: any) => getProjectIdFromRow(s) === pid);
  }, [scopeItems, pid]);

  const paymentsForProject = useMemo(() => {
    return (paymentsReceived || []).filter((p: any) => getProjectIdFromRow(p) === pid);
  }, [paymentsReceived, pid]);

  const subcontractsForProject = useMemo(() => {
    return (subContracts || []).filter((s: any) => getProjectIdFromRow(s) === pid);
  }, [subContracts, pid]);

  const wagesForProject = useMemo(() => {
    return (wageEntries || []).filter((w: any) => getProjectIdFromRow(w) === pid);
  }, [wageEntries, pid]);

  const disbursementsForProject = useMemo(() => {
    return (partnerDisbursements || []).filter((d: any) => getProjectIdFromRow(d) === pid);
  }, [partnerDisbursements, pid]);

  // Enrich API project into the project-like object
  const project: ProjectLike | null = useMemo(() => {
    if (!apiProject) return null;

    const totalEstimate = (scopeForProject || []).reduce(
      (sum: number, s: any) => sum + (Number(s?.estimate) || 0),
      0
    );

    const received = (paymentsForProject || []).reduce(
      (sum: number, r: any) => sum + (Number(r?.amount) || 0),
      0
    );

    return {
      id: apiProject.id,
      clientId: apiProject.client_id,
      name: apiProject.name,
      address: apiProject.address,
      status: (apiProject.status || "Active") as string,
      archived: Number(apiProject.archived) === 1,
      totalEstimate,
      received,
    };
  }, [apiProject, scopeForProject, paymentsForProject]);

  const financials = useMemo(() => {
    const id = project?.id;
    if (!id) return undefined;
    return projectFinancialsByProject?.[id];
  }, [project?.id, projectFinancialsByProject]);

  const goToTab = (tab: TabKey) => {
    setActiveTab(tab);
    navigate(`/projects/${pid}?tab=${queryParamFromTab(tab)}`, { replace: true });
  };

  const renderTab = () => {
    if (!project) return null;

    switch (activeTab) {
      case "Overview":
        if (!financials) {
          return (
            <div className="card">
              <div className="cardHeader">
                <h3 className="cardTitle">{t("projectDetail.tabs.overview")}</h3>
                <div className="cardSub">
                  Financial engine data not available yet (while modules migrate to API).
                </div>
              </div>
              <div className="pageNote">
                You can still use Scope / Payments / Expenses / Wages tabs normally.
              </div>
            </div>
          );
        }
        return (
          <OverviewTab
            project={project as any}
            financials={financials}
            subContracts={subcontractsForProject}
            wages={wagesForProject}
            clients={clients}
            resources={resources}
          />
        );

      case "Scope":
        return (
          <ScopeTab
            projectId={project.id}
            onChanged={() => refreshProjectData(project.id)}
          />
        );

      case "Payments Received":
        return (
          <PaymentsReceivedTab
            projectId={project.id}
            onChanged={() => refreshProjectData(project.id)}
          />
        );

      case "Expenses":
        return (
          <ExpensesTab
            projectId={project.id}
            onChanged={() => refreshProjectData(project.id)}
          />
        );

      case "Wages":
        return <WagesTab wages={wagesForProject} resources={resources} />;

      case "Client Decisions":
        return (
          <ClientDecisionsTab
            projectId={project.id}
            onChanged={() => refreshProjectData(project.id)}
          />
        );

      case "Partner Settlement":
        if (!financials) {
          return (
            <div className="card">
              <div className="cardHeader">
                <h3 className="cardTitle">{t("projectDetail.tabs.partnerSettlement")}</h3>
                <div className="cardSub">
                  Financial engine data not available yet (while modules migrate to API).
                </div>
              </div>
              <div className="pageNote">
                Once we migrate remaining modules (payments/expenses/wages/disbursements) to API,
                settlement will light up again.
              </div>
            </div>
          );
        }
        return (
          <PartnerSettlementTab
            project={project as any}
            financials={financials}
            partners={partners}
            disbursements={disbursementsForProject}
            subContracts={subcontractsForProject}
            wages={wagesForProject}
            addPartnerDisbursement={addPartnerDisbursement}
            updateProject={updateProject}
            onTransferFunds={(method, amount) => {
              navigate(
                `/projects/${pid}?tab=expenses&transfer=in&method=${encodeURIComponent(method)}&amount=${encodeURIComponent(String(amount))}`
              );
              setActiveTab("Expenses");
            }}
          />
        );

      default:
        return null;
    }
  };

  const tabLabel = (tab: TabKey) => {
    switch (tab) {
      case "Overview":
        return t("projectDetail.tabs.overview");
      case "Scope":
        return t("projectDetail.tabs.scope");
      case "Payments Received":
        return t("projectDetail.tabs.paymentsReceived");
      case "Expenses":
        return t("projectDetail.tabs.expenses");
      case "Wages":
        return t("projectDetail.tabs.wages");
      case "Client Decisions":
        return t("projectDetail.tabs.clientDecisions");
      case "Partner Settlement":
        return t("projectDetail.tabs.partnerSettlement");
      default:
        return tab;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <button
          onClick={() => navigate("/projects")}
          className="btn btnSm btnSecondary"
          type="button"
        >
          {t("projectDetail.backToProjects")}
        </button>
        <p style={{ marginTop: 12 }}>{t("projectDetail.loadingProject")}</p>
      </div>
    );
  }

  if (loadError || !project) {
    return (
      <div className="container">
        <button
          onClick={() => navigate("/projects")}
          className="btn btnSm btnSecondary"
          type="button"
        >
          {t("projectDetail.backToProjects")}
        </button>
        <p style={{ marginTop: 12 }}>{loadError || t("projectDetail.projectNotFound")}</p>
      </div>
    );
  }

  const projectLabel = projectDisplayLabelPretty(project as any, clients as any);

  return (
    <div className="container">
      <button
        onClick={() => navigate("/projects")}
        className="btn btnSm btnSecondary"
        style={{ marginBottom: 10 }}
        type="button"
      >
        {t("projects.backToProjects")}
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <h2 style={{ margin: 0 }}>{projectLabel}</h2>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(
            [
              "Overview",
              "Scope",
              "Payments Received",
              "Expenses",
              "Wages",
              "Client Decisions",
              "Partner Settlement",
            ] as TabKey[]
          ).map((tab) => (
            <button
              key={tab}
              className={`btn btnSm ${activeTab === tab ? "" : "btnSecondary"}`}
              onClick={() => goToTab(tab)}
              type="button"
            >
              {tabLabel(tab)}
            </button>
          ))}
        </div>
      </div>

      {renderTab()}
    </div>
  );
}
